import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, MenuItem, Stack, Pagination, Chip, IconButton,
  Tooltip, CircularProgress, Snackbar, Alert, Tabs, Tab, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  getStockMovements,
  confirmPendingStockMovements,
  cancelPendingStockMovements,
} from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormStockMovement from './FormStockMovement';
import { formatDateTimeLocale } from '../utils/datetimeLocal';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { getApiErrorMessage } from '../utils/apiError';

const TIPOS = [
  { id: 1, nome: 'Compra' },
  { id: 2, nome: 'Venda' },
  { id: 3, nome: 'Devolução Cliente' },
  { id: 4, nome: 'Devolução Fornecedor' },
  { id: 5, nome: 'Ajuste +' },
  { id: 6, nome: 'Ajuste -' },
  { id: 7, nome: 'Inventário' },
];

function formatDataLocal(iso) {
  return formatDateTimeLocale(iso);
}

function StockMovementList() {
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const [aba, setAba] = useState(0); // 0=confirmados, 1=pendentes
  const pendente = aba === 1;

  const [pagina, setPagina] = useState(1);
  const [qtde] = useState(20);

  const [termo, setTermo] = useState('');
  const [termoEmUso, setTermoEmUso] = useState('');
  const [fluxo, setFluxo] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');

  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [selecionados, setSelecionados] = useState(() => new Set());

  const [form, setForm] = useState({ open: false, id: null });
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const [confirmDlg, setConfirmDlg] = useState({ open: false, acao: null });
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const resp = await getStockMovements({
        indice: pagina,
        qtde,
        fluxo,
        tipoId,
        termo: termoEmUso,
        de: de || '',
        ate: ate || '',
        pendente,
        estabelecimentoId: estabelecimentoId || '',
      });
      const data = resp.data || {};
      setItens(data.items || []);
      setTotal(data.totalItems || 0);
      setTotalPaginas(data.totalPages || 1);
      setSelecionados(new Set());
    } finally {
      setCarregando(false);
    }
  }, [pagina, qtde, fluxo, tipoId, termoEmUso, de, ate, pendente, estabelecimentoId]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { setPagina(1); setSelecionados(new Set()); }, [fluxo, tipoId, de, ate, aba]);

  const idsPagina = useMemo(() => itens.map((m) => m.id), [itens]);
  const todosSelecionados =
    idsPagina.length > 0 && idsPagina.every((id) => selecionados.has(id));
  const algunsSelecionados =
    idsPagina.some((id) => selecionados.has(id)) && !todosSelecionados;

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosSelecionados) {
        idsPagina.forEach((id) => next.delete(id));
      } else {
        idsPagina.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleUm = (id) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doBuscar = () => { setPagina(1); setTermoEmUso(termo); };
  const onKeyDown = (e) => { if (e.key === 'Enter') doBuscar(); };

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnackMsg(msg);
    setSnackSeverity('success');
    setSnackOpen(true);
    carregar();
  };

  const pedirConfirmacao = (acao) => {
    if (selecionados.size === 0) {
      setSnackMsg('Selecione ao menos um movimento.');
      setSnackSeverity('warning');
      setSnackOpen(true);
      return;
    }
    setConfirmDlg({ open: true, acao });
  };

  const executarAcao = async () => {
    const acao = confirmDlg.acao;
    const ids = Array.from(selecionados);
    setConfirmDlg({ open: false, acao: null });
    setProcessando(true);
    try {
      if (acao === 'confirmar') {
        const resp = await confirmPendingStockMovements(ids);
        const n = resp.data?.confirmados ?? ids.length;
        setSnackMsg(`${n} movimento(s) confirmado(s).`);
      } else {
        const resp = await cancelPendingStockMovements(ids);
        const n = resp.data?.cancelados ?? ids.length;
        setSnackMsg(`${n} movimento(s) cancelado(s).`);
      }
      setSnackSeverity('success');
      setSnackOpen(true);
      await carregar();
    } catch (err) {
      setSnackMsg(getApiErrorMessage(err, 'Falha ao processar.'));
      setSnackSeverity('error');
      setSnackOpen(true);
    } finally {
      setProcessando(false);
    }
  };

  const chipFluxo = (f) => {
    const isEntrada = ['E', 'Entrada', '0'].includes(String(f));
    return (
      <Chip
        size="small"
        label={isEntrada ? 'Entrada' : 'Saída'}
        color={isEntrada ? 'success' : 'error'}
        variant="outlined"
      />
    );
  };

  const colSpan = pendente ? 9 : 8;
  const movementAccessors = useMemo(() => ({
    data: (m) => m.data,
    quantidade: (m) => Number(m.quantidade) || 0,
  }), []);
  const { sorted: itensOrdenados, orderBy, order, requestSort } = useClientSort(
    itens,
    'data',
    'desc',
    movementAccessors,
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Movimentos de estoque"
        subtitle="Entradas e saídas registradas no ZenGestão e sincronizadas dos PDVs."
        actions={
          <>
            <Tooltip title="Recarregar">
              <IconButton onClick={carregar}><RefreshIcon /></IconButton>
            </Tooltip>
            {!pendente && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => abrirForm()}
              >
                Novo movimento
              </Button>
            )}
          </>
        }
      />

      <Tabs
        value={aba}
        onChange={(_e, v) => setAba(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Movimentações" />
        <Tab label="Movimentações pendentes" />
      </Tabs>

      {pendente && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            onClick={toggleTodos}
            disabled={!itens.length || carregando}
          >
            {todosSelecionados ? 'Desmarcar página' : 'Selecionar tudo'}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            disabled={selecionados.size === 0 || processando}
            onClick={() => pedirConfirmacao('confirmar')}
          >
            Confirmar ({selecionados.size})
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            disabled={selecionados.size === 0 || processando}
            onClick={() => pedirConfirmacao('cancelar')}
          >
            Cancelar ({selecionados.size})
          </Button>
        </Stack>
      )}

      <Paper
        variant="outlined"
        component={Stack}
        direction="row"
        spacing={2}
        sx={{ p: 2, mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          placeholder="Buscar por produto ou usuário"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={onKeyDown}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 280 }}
        />
        <Button variant="outlined" onClick={doBuscar}>Buscar</Button>

        <TextField
          select size="small" label="Fluxo" value={fluxo}
          onChange={(e) => setFluxo(e.target.value)} sx={{ minWidth: 140 }}
        >
          <MenuItem value=""><em>Todos</em></MenuItem>
          <MenuItem value="E">Entradas</MenuItem>
          <MenuItem value="S">Saídas</MenuItem>
        </TextField>

        <TextField
          select size="small" label="Tipo" value={tipoId}
          onChange={(e) => setTipoId(e.target.value)} sx={{ minWidth: 220 }}
        >
          <MenuItem value=""><em>Todos</em></MenuItem>
          {TIPOS.map(t => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
        </TextField>

        <TextField
          size="small" label="De" type="date" value={de}
          onChange={(e) => setDe(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" label="Até" type="date" value={ate}
          onChange={(e) => setAte(e.target.value)} InputLabelProps={{ shrink: true }}
        />
      </Paper>

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              {pendente && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={todosSelecionados}
                    indeterminate={algunsSelecionados}
                    onChange={toggleTodos}
                    disabled={!itens.length}
                  />
                </TableCell>
              )}
              <SortableTh id="data" label="Data" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="produtoNome" label="Produto" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="usuarioNome" label="Usuário" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="fluxo" label="Fluxo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="tipoNome" label="Tipo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="quantidade" label="Quantidade" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="observacao" label="Observação" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="referencia" label="Referência" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : itens.length ? (
              itensOrdenados.map((m) => (
                <TableRow key={m.id} hover selected={pendente && selecionados.has(m.id)}>
                  {pendente && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selecionados.has(m.id)}
                        onChange={() => toggleUm(m.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell data-label="Data">{formatDataLocal(m.data)}</TableCell>
                  <TableCell data-label="Produto">{m.produtoNome}</TableCell>
                  <TableCell data-label="Usuário">{m.usuarioNome}</TableCell>
                  <TableCell data-label="Fluxo">{chipFluxo(m.fluxo)}</TableCell>
                  <TableCell data-label="Tipo">{m.tipoNome}</TableCell>
                  <TableCell align="right" data-label="Quantidade">
                    {Number(m.quantidade).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell data-label="Observação">{m.observacao || '-'}</TableCell>
                  <TableCell data-label="Referência">{m.referencia || '-'}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {pendente
                    ? 'Nenhuma movimentação pendente'
                    : 'Nenhum movimento encontrado'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={1} sx={{ mt: 2 }} alignItems="center">
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {itens.length} de {total} movimentos (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title="Novo movimento de estoque"
        subtitle={null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <FormStockMovement
          key="novo"
          embedded
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      <Dialog open={confirmDlg.open} onClose={() => setConfirmDlg({ open: false, acao: null })}>
        <DialogTitle>
          {confirmDlg.acao === 'confirmar' ? 'Confirmar movimentos?' : 'Cancelar movimentos?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDlg.acao === 'confirmar'
              ? `Confirmar ${selecionados.size} movimento(s) pendente(s)? O estoque será atualizado.`
              : `Cancelar ${selecionados.size} movimento(s) pendente(s)? Eles serão excluídos e o estoque não muda.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg({ open: false, acao: null })} color="inherit">
            Não
          </Button>
          <Button
            onClick={executarAcao}
            color={confirmDlg.acao === 'confirmar' ? 'success' : 'error'}
            variant="contained"
            autoFocus
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}

export default StockMovementList;
